import { cloneDeep } from 'lodash';
import { type IFramework } from '@/framework';
import { TextMeasurer } from '@/utils/text-kit';
import {
    transformArtTextDataToStage,
    transformGlowToStage,
    transformLetterSpacingToStage,
    transformShadowToStage,
    transformStrokeToStage,
} from '@/utils/transform-kit';
import {
    ITextContent,
    MaterialIdsType,
    TextWritingType,
    type EditingRange,
    type IAdvancedTextMaterial,
    type ILayerProps,
    type ITextLayerProps,
} from '@/version-types';
import { type FormatBrushData } from '../types';
import {
    createTextContentsByArtText,
    getLayerFontFamilyIds,
    isSingleCharacterSegmenter,
    pushHistory,
    updateLayer,
    validateInputLayer,
} from '../utils';

export type ArtTextCharacterConfig = Partial<
    Pick<
        ITextContent,
        'color' | 'bold' | 'italic' | 'lineThrough' | 'underline' | 'rotate' | 'scale' | 'offsetX' | 'offsetY'
    >
>;

export interface IArtText {
    /**
     * 应用花字效果
     * @param layerID - 图层 ID
     * @param data - 花字特效数据
     * @returns
     */
    applyArtText(
        layerID: string,
        data: IAdvancedTextMaterial,
        options?: { writeHistory?: boolean; formatBrushData?: FormatBrushData },
    ): Promise<ILayerProps | void>;

    /**
     * 取消花字效果
     * @param layerID - 图层 ID
     * @returns
     */
    cancelArtText(layerID: string): ILayerProps | void;

    /**
     * 调整单字符花字效果
     * ATTENTION: 仅应用于设计师编辑器
     *
     * @param layerID - 图层 ID
     * @param range - 选区范围
     * @param options - 配置项
     *  options.partialConfig - 部分配置
     *  options.writeHistory - 是否写入历史记录
     */
    __adjustArtTextSingleCharacter(
        layerID: string,
        partialConfig: ArtTextCharacterConfig,
        options: {
            range: EditingRange | null;
            writeHistory?: boolean;
        },
    ): ILayerProps | void;
}

export class ArtText implements IArtText {
    constructor(private framework: IFramework) {}

    private prevLayer: ILayerProps | null = null;

    /**
     * 应用花字基础属性
     *
     * @param layerData - 原始文本图层数据
     * @param data - 花字特效数据
     * @param ratio - 字体大小比例
     * @returns
     */
    private attachBaseProps(layerData: ITextLayerProps, data: IAdvancedTextMaterial, _: number) {
        const standardTextContent = data.textContents[0];

        const fontFamily = standardTextContent.fontFamily;
        const letterSpacing = transformLetterSpacingToStage(data.material.defaultLetterSpacing, layerData.fontSize);

        const nextLayerData: ITextLayerProps = {
            ...layerData,
            fontFamily,
            letterSpacing,
        };

        return nextLayerData;
    }

    /**
     * 应用花字特效属性
     *
     * @param layerData - 原始文本图层数据
     * @param data - 花字特效数据
     * @param ratio - 字体大小比例
     * @returns
     */
    private attachEffectProps(layerData: ITextLayerProps, data: IAdvancedTextMaterial, ratio: number) {
        // 重置花字可能影响的特性
        const nextLayerData: ITextLayerProps = { ...layerData, textStroke: [], textShadow: [], textGlow: [] };

        const standardTextContent = data.textContents[0];

        const textStrokes = standardTextContent.textStroke;
        if (textStrokes && textStrokes.length > 0) {
            let nextTextStrokes = transformStrokeToStage(textStrokes, data.material.defaultFontSize);
            nextTextStrokes = nextTextStrokes.map((item) => ({
                ...item,
                width: Math.round(item.width * ratio),
            }));
            nextLayerData.textStroke = nextTextStrokes;
        }

        const textShadows = standardTextContent.textShadow;
        if (textShadows && textShadows.length > 0) {
            let nextTextShadows = transformShadowToStage(textShadows, data.material.defaultFontSize);
            nextTextShadows = nextTextShadows.map((item) => ({
                ...item,
                x: Math.round(item.x * ratio),
                y: Math.round(item.y * ratio),
            }));

            nextLayerData.textShadow = nextTextShadows;
        }

        const textGlows = standardTextContent.textGlow;
        if (textGlows && textGlows.length > 0) {
            let nextTextGlows = transformGlowToStage(textGlows, data.material.defaultFontSize);
            nextTextGlows = nextTextGlows.map((item) => ({
                ...item,
                width: Math.round(item.width * ratio),
            }));

            nextLayerData.textGlow = nextTextGlows;
        }

        return nextLayerData;
    }

    private resetTextContents(layerData: ITextLayerProps) {
        const { textContents } = layerData;
        if (!textContents || textContents.length === 0) return layerData;

        const nextTextContents = textContents.map((item) => {
            const ret = { ...item };
            const dropped: (keyof ITextContent)[] = ['rotate', 'offsetX', 'offsetY', 'scale'];
            dropped.forEach((key) => {
                if (Reflect.has(ret, key)) Reflect.deleteProperty(ret, key);
            });
            return ret;
        });

        return { ...layerData, textContents: nextTextContents };
    }

    private calcLayout(layer: ILayerProps) {
        // const layerData = layer.data as ITextLayerProps;
        // const { writingMode, fontSize, lineSpacing } = layerData;

        // const keepConstantWidth =
        //     writingMode === TextWritingType.HORIZONTAL_TB &&
        //     (get(layer, '__fixedDirectionSize', false) ||
        //         checkIsHorizontalMultiLine(layer.height, fontSize, lineSpacing));

        const keepConstantWidth = true;
        const keepConstantHeight = false;
        const textSize = TextMeasurer.measure(layer, { keepConstantWidth, keepConstantHeight });

        return {
            width: textSize.width,
            height: textSize.height,
        };
    }

    async applyArtText(
        layerID: string,
        data: IAdvancedTextMaterial,
        options?: { writeHistory?: boolean; formatBrushData?: FormatBrushData },
    ) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const { writeHistory = true, formatBrushData } = options || {};
        const { id, material } = data;

        const prevLayerData = prevLayer.data as ITextLayerProps;

        if (prevLayerData.advancedTextMaterial && prevLayerData.advancedTextMaterial.id === id) {
            return;
        }

        const ratio = prevLayerData.fontSize / material.defaultFontSize;

        // --- 1. 设置基础属性 (ATTENTION: 顺序不可变) ---
        let nextLayerData = cloneDeep(prevLayer.data) as ITextLayerProps;
        nextLayerData = this.resetTextContents(nextLayerData);
        nextLayerData = this.attachBaseProps(nextLayerData, data, ratio);
        nextLayerData = this.attachEffectProps(nextLayerData, data, ratio);
        nextLayerData = { ...nextLayerData, advancedTextMaterial: transformArtTextDataToStage(data) }; // 此时需要再将底层数据转换为舞台数据
        nextLayerData = {
            ...nextLayerData,
            textContents: createTextContentsByArtText(prevLayerData.text, nextLayerData),
        };

        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const nextExtraInfo =
            prevLayer.extraInfo && Object.prototype.toString.call(prevLayer.extraInfo) === '[object Object]'
                ? { ...prevLayer.extraInfo }
                : {};

        if (formatBrushData) {
            const overrideColorInArtTextCase = formatBrushData.__overrideColorInArtTextCase;

            nextLayerData = {
                ...nextLayerData,
                ...formatBrushData.data,
                textContents: nextLayerData.textContents!.map((item) => ({
                    ...item,
                    fontSize: formatBrushData.data.fontSize,
                    color: overrideColorInArtTextCase ? overrideColorInArtTextCase : item.color,
                })),
            };
            nextLayer.data = nextLayerData;
            nextLayer.opacity = formatBrushData.opacity;

            if (overrideColorInArtTextCase) {
                nextExtraInfo.overrideColorInArtTextCase = overrideColorInArtTextCase;
            }

            if (nextLayerData.shapeExtension) {
                Reflect.deleteProperty(nextLayer.data, 'shapeExtension');
                Reflect.deleteProperty(nextLayer.data, 'screenshotUrl');
                if (nextLayer.materialIds)
                    Reflect.deleteProperty(nextLayer.materialIds, MaterialIdsType.TEXT_TRANSFORM);
            }

            if (nextLayerData.writingMode !== TextWritingType.HORIZONTAL_TB) {
                Reflect.set(nextLayer.data, 'writingMode', TextWritingType.HORIZONTAL_TB);
            }
        }

        // --- 2. 注册字体 ---
        await this.framework.registerFont(nextLayer, nextLayerData.fontFamily, true);

        // --- 3. 更新素材 ID ---
        const nextMaterialIDMap = nextLayer.materialIds ? cloneDeep(nextLayer.materialIds) : {};
        const nextFontFamilyIds = getLayerFontFamilyIds(this.framework, nextLayer);

        nextMaterialIDMap[MaterialIdsType.FONT_FAMILY] = nextFontFamilyIds;
        nextMaterialIDMap[MaterialIdsType.ADVANCED_TEXT_MATERIAL] = [id];

        // 若原本有局部划重点的情况下，在应用花字后会把局部划重点清空
        // 所以这里需要根据一级 textMark 来判断是否包含划重点并以此来更新 materialIds
        if (nextLayerData.textMark) {
            nextMaterialIDMap[MaterialIdsType.TEXT_MARK] = [nextLayerData.textMark.materialId];
        } else {
            Reflect.deleteProperty(nextMaterialIDMap, MaterialIdsType.TEXT_MARK);
        }

        if (formatBrushData && formatBrushData.materialIds) {
            if (formatBrushData.materialIds[MaterialIdsType.TEXT_EFFECT]) {
                nextMaterialIDMap[MaterialIdsType.TEXT_EFFECT] =
                    formatBrushData.materialIds[MaterialIdsType.TEXT_EFFECT];
            }
        }

        nextLayer.materialIds = nextMaterialIDMap;

        // --- 4. 更新尺寸 ---
        const { width, height } = this.calcLayout(nextLayer);
        nextLayer.width = width;
        nextLayer.height = height;

        // --- 5. 更新 extraInfo ---
        nextLayer.extraInfo = nextExtraInfo;

        updateLayer(this.framework, nextLayer);
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer);

        return nextLayer;
    }

    cancelArtText(layerID: string) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (!prevLayerData.advancedTextMaterial) return;

        const nextLayerData: ITextLayerProps = {
            ...this.resetTextContents(cloneDeep(prevLayerData)),
            textStroke: [],
            textShadow: [],
            textGlow: [],
        };
        Reflect.deleteProperty(nextLayerData, 'advancedTextMaterial');

        const nextMaterialIDMap = prevLayer.materialIds ? cloneDeep(prevLayer.materialIds) : {};
        Reflect.deleteProperty(nextMaterialIDMap, MaterialIdsType.ADVANCED_TEXT_MATERIAL);

        const nextExtraInfo = prevLayer.extraInfo ? cloneDeep(prevLayer.extraInfo) : {};
        Reflect.deleteProperty(nextExtraInfo, 'overrideColorInArtTextCase');

        const nextLayer: ILayerProps = {
            ...prevLayer,
            data: nextLayerData,
            materialIds: nextMaterialIDMap,
            extraInfo: nextExtraInfo,
        };

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer);

        return nextLayer;
    }

    __adjustArtTextSingleCharacter(
        layerID: string,
        partialConfig: ArtTextCharacterConfig,
        options: {
            range: EditingRange;
            writeHistory?: boolean;
        },
    ) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!validateInputLayer(prevLayer)) return;

        const { range, writeHistory = false } = options || {};

        const prevLayerData = prevLayer.data as ITextLayerProps;

        const { textContents: prevTextContents = [], advancedTextMaterial: prevAdvancedTextMaterial } = prevLayerData;
        if (prevTextContents.length === 0 || !prevAdvancedTextMaterial) return;

        const nextAdvancedTextMaterial: IAdvancedTextMaterial = cloneDeep(prevAdvancedTextMaterial);
        let nextTextContents: ITextContent[] = prevTextContents;

        if (!range) {
            nextAdvancedTextMaterial.textContents = nextAdvancedTextMaterial.textContents.map((item) => {
                return {
                    ...item,
                    ...partialConfig,
                };
            });

            nextTextContents = prevTextContents.map((item) => {
                return {
                    ...item,
                    ...partialConfig,
                };
            });
        } else {
            const { startIndex, endIndex } = range;
            if (startIndex !== endIndex) return;

            const targetTextContent = prevTextContents[startIndex];
            if (!targetTextContent || !isSingleCharacterSegmenter(targetTextContent.text)) return;

            const useCount = prevAdvancedTextMaterial.material.useCount;
            if (!useCount) return;

            nextAdvancedTextMaterial.textContents[startIndex % useCount] = {
                ...nextAdvancedTextMaterial.textContents[startIndex % useCount],
                ...partialConfig,
            };

            nextTextContents = prevTextContents.map((item, index) => {
                if (index % useCount !== startIndex % useCount) return item;
                return {
                    ...item,
                    ...partialConfig,
                };
            });
        }

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            advancedTextMaterial: nextAdvancedTextMaterial,
            textContents: nextTextContents,
        };

        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer);
            this.prevLayer = null;
        }

        return nextLayer;
    }
}
